import { z } from 'zod';

import { TimestampSchema, UidSchema } from './primitives.js';
import { DEFAULT_TOYYIBPAY_FEE_TABLE } from '../payments/toyyibpay-fee.js';
import { DEFAULT_AIRWALLEX_FEES } from '../payments/airwallex-fee.js';

/**
 * `platformConfig/moneyPolicy` — the super-admin-editable home for every
 * money number that is NOT a plan price and NOT an agent commission rule.
 *
 * Prices live in `platformConfig/plans/items/{planId}` (`PlanSchema`);
 * commission/payout rules live in `platformConfig/commissionRules`
 * (`CommissionRulesSchema`). This doc holds the three remaining families that
 * were code literals until ADR-0033:
 *
 *   - `trial.days`          — how long a newly provisioned tenant gets free
 *   - `psp.toyyibpay.*`     — the payment-gateway fee table the customer sees
 *   - `billingLifecycle.*`  — grace / suspend / retention / invoice-due windows
 *
 * ── THE DEFAULTING RULE (ADR-0033) ──────────────────────────────────────
 * The house rule is "a tunable value defaults to off/0". Read literally that
 * would make a missing doc mean *no trial* and *Kilat absorbs the PSP fee* —
 * a pricing event caused by an unreachable document. So the rule is split,
 * and the split is deliberate:
 *
 *   1. A NEW money layer that paid nobody yesterday defaults to 0 / off.
 *      (That is why `CommissionRulesSchema.survivalBonusMultiplier` is 0.)
 *   2. EXISTING behaviour being made configurable defaults to exactly the
 *      literal that shipped — trial 14 days, FPX 100 sen, card 250bp + 100
 *      sen capped at 5000 sen, lifecycle 0/30/90, invoice due 14 days.
 *
 * The rule exists so the platform never silently STARTS paying someone. It is
 * not a licence to silently stop charging a fee or to cancel a trial. **A
 * missing config document is a config problem, not a pricing event.** Anyone
 * "fixing" these defaults to zero should read ADR-0033 first.
 */

/** Free-trial policy applied at tenant provisioning. */
export const TrialPolicySchema = z
  .object({
    days: z
      .number()
      .int()
      .min(0)
      .max(365)
      .default(14)
      .describe(
        'Free-trial length in days stamped onto subscription.trialEndsAt at provisioning. ' +
          '0 = no trial (bills immediately). Shipped default: 14.',
      ),
  })
  .describe('Free-trial policy for newly provisioned tenants');
export type TrialPolicy = z.infer<typeof TrialPolicySchema>;

/**
 * toyyibPay's fee table, as charged to the CUSTOMER (zero-cut principle,
 * ADR-0019 — the operator always receives their stated price). Mirrors the
 * PSP's published pricing rather than Kilat policy, which is exactly why it
 * has to be editable: toyyibPay changing their rates must not be a deploy.
 */
export const ToyyibPayFeePolicySchema = z
  .object({
    fpxFlatSen: z
      .number()
      .int()
      .min(0)
      .max(100_000)
      .default(DEFAULT_TOYYIBPAY_FEE_TABLE.fpxFlatSen)
      .describe('Flat sen charged on an FPX payment. Shipped default: 100 (RM 1.00).'),
    cardPctBasisPoints: z
      .number()
      .int()
      .min(0)
      .max(10_000)
      .default(DEFAULT_TOYYIBPAY_FEE_TABLE.cardPctBasisPoints)
      .describe(
        'Card percentage in basis points — 250 = 2.5%. Integer so no float enters money math. ' +
          'Shipped default: 250.',
      ),
    cardFlatSen: z
      .number()
      .int()
      .min(0)
      .max(100_000)
      .default(DEFAULT_TOYYIBPAY_FEE_TABLE.cardFlatSen)
      .describe('Flat sen added on top of the card percentage. Shipped default: 100 (RM 1.00).'),
    cardCapSen: z
      .number()
      .int()
      .min(0)
      .max(1_000_000)
      .default(DEFAULT_TOYYIBPAY_FEE_TABLE.cardCapSen)
      .describe(
        'Ceiling on the TOTAL card fee (percentage + flat), in sen. Shipped default: 5000 (RM 50.00).',
      ),
  })
  .describe('toyyibPay fee table used to quote the customer-visible "Payment fee" line');
export type ToyyibPayFeePolicy = z.infer<typeof ToyyibPayFeePolicySchema>;

/**
 * Airwallex fee table (ADR-0043 — the rail that replaces toyyibPay).
 *
 * Same override mechanism as the toyyibPay table above, with one addition that
 * matters: `confirmed`. toyyibPay's defaults were the rates that had actually
 * shipped, so ADR-0033's rule — "an existing behaviour keeps the literal it
 * shipped with" — had something real to preserve. Airwallex has no shipped
 * literal: no account exists, and Airwallex prices per merchant.
 *
 * So the defaults here are a placeholder that must not be mistaken for a
 * decision, and `confirmed` is how they say so. It travels inside the table
 * rather than beside it, so it cannot be left behind when a super-admin
 * overrides the numbers.
 */
export const AirwallexFeePolicySchema = z
  .object({
    localPctBasisPoints: z
      .number()
      .int()
      .min(0)
      .max(10_000)
      .default(DEFAULT_AIRWALLEX_FEES.localPctBasisPoints)
      .describe(
        'Local-method (FPX / DuitNow QR) percentage in basis points — 180 = 1.8%. ' +
          'UNCONFIRMED placeholder until the owner has an Airwallex rate card.',
      ),
    localFlatSen: z
      .number()
      .int()
      .min(0)
      .max(100_000)
      .default(DEFAULT_AIRWALLEX_FEES.localFlatSen)
      .describe('Flat sen added on top of the local-method percentage. Placeholder default: 0.'),
    cardPctBasisPoints: z
      .number()
      .int()
      .min(0)
      .max(10_000)
      .default(DEFAULT_AIRWALLEX_FEES.cardPctBasisPoints)
      .describe('Card percentage in basis points — 290 = 2.9%. UNCONFIRMED placeholder.'),
    cardFlatSen: z
      .number()
      .int()
      .min(0)
      .max(100_000)
      .default(DEFAULT_AIRWALLEX_FEES.cardFlatSen)
      .describe('Flat sen added on top of the card percentage. Placeholder default: 50 (RM 0.50).'),
    capSen: z
      .number()
      .int()
      .min(0)
      .max(1_000_000)
      .default(DEFAULT_AIRWALLEX_FEES.capSen)
      .describe(
        'Ceiling on the TOTAL fee for one payment, in sen. A safety rail against a mistyped ' +
          'basis-point value, not a published Airwallex cap. Default: 5000 (RM 50.00).',
      ),
    confirmed: z
      .boolean()
      .default(DEFAULT_AIRWALLEX_FEES.confirmed)
      .describe(
        'Has a human checked these against the real Airwallex rate card? Defaults to FALSE. ' +
          'Launch readiness treats unconfirmed PSP pricing as a blocker, because the platform ' +
          'must not charge a customer a fee nobody has verified.',
      ),
  })
  .describe('Airwallex fee table used to quote the customer-visible "Payment fee" line (ADR-0043)');
export type AirwallexFeePolicy = z.infer<typeof AirwallexFeePolicySchema>;

/** Payment-service-provider fee tables, one key per gateway. */
/**
 * Which gateway is actually taking money today (ADR-0043).
 *
 * A NAME, not a feature flag, because exactly one rail is live at a time and
 * "both on" is not a coherent state: it would mean two reconciliation paths and
 * a settlement question every time two figures disagree.
 *
 * Only the ACTIVE provider's fee table quotes the customer's `Payment fee`
 * line, which is why the selector and the tables live in the same document —
 * splitting them across two collections is how a provider ends up charging with
 * the other one's rates.
 */
export const PaymentProviderSchema = z.enum(['toyyibpay', 'airwallex']);
export type PaymentProvider = z.infer<typeof PaymentProviderSchema>;

/**
 * Providers whose code actually exists.
 *
 * Airwallex is CONFIGURABLE but not implemented: its fee table, schema
 * discriminator and panel entry are real; its client, webhook verification and
 * billing rail are not. Selecting it must therefore be refused rather than
 * accepted and quietly broken.
 *
 * This repo keeps relearning that lesson the expensive way — a "coming soon"
 * points-expiry control whose engine had already shipped, a `-Xmx4g` fix that
 * was causing the crash it was meant to prevent. A provider dropdown that
 * accepts a value the code cannot honour is the same shape: a control that
 * lies, discovered by a customer at the moment they try to pay.
 */
export const IMPLEMENTED_PAYMENT_PROVIDERS: readonly PaymentProvider[] = ['toyyibpay'];

export const PspPolicySchema = z
  .object({
    /**
     * The live rail. Defaults to toyyibPay because that is what ships and what
     * is wired end to end — a missing or unreadable policy document must never
     * silently move the platform onto an unimplemented gateway.
     */
    activeProvider: PaymentProviderSchema.default('toyyibpay'),
    toyyibpay: ToyyibPayFeePolicySchema.default({}),
    airwallex: AirwallexFeePolicySchema.default({}),
  })
  .describe('Per-gateway fee tables plus the one that is currently live (ADR-0043)');
export type PspPolicy = z.infer<typeof PspPolicySchema>;

/**
 * Subscription-lifecycle windows. Consumed by `decideTenantLifecycle`
 * (`packages/shared/src/billing/tenant-lifecycle.ts`) via the runner in
 * `apps/functions/src/billing/tenant-lifecycle-runner.ts`, plus the invoice
 * due date stamped by `monthlyBilling`.
 */
export const BillingLifecyclePolicySchema = z
  .object({
    pastDueGraceDays: z
      .number()
      .int()
      .min(0)
      .max(90)
      .default(0)
      .describe(
        'Days past an invoice dueDate before the tenant flips active → past_due. ' +
          'Shipped default: 0 (past due the moment the due date passes).',
      ),
    suspendAfterDays: z
      .number()
      .int()
      .min(0)
      .max(365)
      .default(30)
      .describe(
        'Days an invoice may stay overdue before the tenant flips past_due → suspended. Shipped default: 30.',
      ),
    suspendedRetentionDays: z
      .number()
      .int()
      .min(1)
      .max(3_650)
      .default(90)
      .describe(
        'Days a suspended tenant is retained before archiving. Shipped default: 90. ' +
          'Minimum 1 — archiving is destructive, so "immediately" is not an option.',
      ),
    invoiceDueDays: z
      .number()
      .int()
      .min(0)
      .max(90)
      .default(14)
      .describe(
        'Days after period close that a generated subscription invoice falls due. Shipped default: 14.',
      ),
  })
  .describe('Grace / suspend / retention / invoice-due windows for operator subscriptions');
export type BillingLifecyclePolicy = z.infer<typeof BillingLifecyclePolicySchema>;

/**
 * Kilat's OWN service-tax status as the SUPPLIER of the operator subscription.
 *
 * On the monthly subscription invoice Kilat sells and the operator buys. Under
 * Malaysian SST, whether service tax is charged depends on the SUPPLIER's
 * registration — Kilat's — never on the customer's. Until 2026-10 the invoice
 * run added SST whenever the OPERATOR had ticked "I'm registered", which had
 * the parties backwards: charging SST while unregistered is an offence, and
 * once registered the tax is owed whatever the operator's status.
 *
 * Defaults to NOT registered. This is a new money layer in ADR-0033's sense —
 * it starts charging somebody — so it is off until the owner fills it in from
 * the super-admin panel. The supplier's legal name / registration / address
 * are NOT duplicated here; they live on the platform brand (`legal`).
 */
export const SupplierTaxPolicySchema = z
  .object({
    sstRegistered: z
      .boolean()
      .default(false)
      .describe(
        'Is the platform operator (the legal entity on platformBrands/{id}.legal) registered for ' +
          'SST with Royal Malaysian Customs? Default FALSE — no SST is charged on any operator ' +
          'subscription invoice until this is switched on.',
      ),
    sstNumber: z
      .string()
      .trim()
      .max(40)
      .default('')
      .describe('The SST registration number printed on tax invoices. Required when registered.'),
    sstRate: z
      .number()
      .min(0)
      .max(1)
      .default(0.08)
      .describe('Service-tax rate as a decimal — 0.08 = 8%. Only applied when registered.'),
  })
  .superRefine((v, ctx) => {
    if (v.sstRegistered && v.sstNumber.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sstNumber'],
        message: 'An SST number is required when the platform is marked SST-registered.',
      });
    }
  })
  .describe("The platform's own SST status as supplier of the operator subscription");
export type SupplierTaxPolicy = z.infer<typeof SupplierTaxPolicySchema>;

/**
 * The SST rate to apply to an operator subscription invoice: the platform's
 * configured rate when it is registered (with a number), otherwise 0. The
 * operator's own tax registration is deliberately NOT an input.
 */
export function supplierSstRate(policy: SupplierTaxPolicy): number {
  return policy.sstRegistered && policy.sstNumber.trim().length > 0 ? policy.sstRate : 0;
}

/**
 * The stored policy doc. Every field defaults, so `MoneyPolicySchema.parse({})`
 * reproduces today's shipped behaviour exactly — that property is asserted in
 * `money-policy.test.ts` and is what makes the server's "parse failed, use the
 * seed" fallback safe.
 */
export const MoneyPolicySchema = z
  .object({
    trial: TrialPolicySchema.default({}),
    psp: PspPolicySchema.default({}),
    billingLifecycle: BillingLifecyclePolicySchema.default({}),
    supplierTax: SupplierTaxPolicySchema.default({}),
    updatedAt: TimestampSchema.optional().describe('Last policy write'),
    updatedBy: UidSchema.optional().describe('Super-admin uid who last edited the policy'),
  })
  .describe('platformConfig/moneyPolicy — admin-editable money values (ADR-0033)');
export type MoneyPolicy = z.infer<typeof MoneyPolicySchema>;
